import { useEffect, useState } from "react";
import { api, fileUrl } from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Sparkles, Trash2, Wand2 } from "lucide-react";
import TokenAnimationMakerModal from "./TokenAnimationMakerModal";

// Thumbnail grid of every token/map image uploaded to this game (plus anything the GM has
// promoted to their cross-campaign Master Pool from the home-page Assets library) — so art gets
// reused with one click instead of a fresh upload every time.
export default function GmAssetsTab({ game, scene, onSpawnToken }) {
  const [assets, setAssets] = useState([]);
  const [kindFilter, setKindFilter] = useState("token");
  const [animMakerOpen, setAnimMakerOpen] = useState(false);
  const [hasGmPass, setHasGmPass] = useState(false);

  const load = () => { api.get("/uploads", { params: { game_id: game.id, kind: kindFilter } }).then((r) => setAssets(r.data)); };
  useEffect(load, [game.id, kindFilter]);
  useEffect(() => { api.get("/billing/me").then((r) => setHasGmPass(!!r.data?.has_gm_entitlement)).catch(() => {}); }, []);

  const spawnAsNpc = (asset) => {
    if (!scene) return toast.error("No active scene to spawn onto");
    onSpawnToken({ name: asset.original_filename?.split(".")[0] || "New NPC", token_type: "npc", image_url: fileUrl(asset.id) });
    toast.success(`Spawned "${asset.original_filename}" onto ${scene.name}`);
  };

  const deleteAsset = async (id) => {
    await api.delete(`/uploads/${id}`);
    load();
  };

  return (
    <div className="space-y-3">
      {hasGmPass && (
        <div className="bg-black/20 border border-white/10 rounded-xl p-3 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Animation Maker</p>
            <p className="text-[10px] text-[#f1e0c5]/30">Build 2D sprite-sheet animated tokens (idle/walk/hit/death/resurrection/attack).</p>
          </div>
          <Button data-testid="open-animation-maker-assets-tab" size="sm" className="rounded-full gap-1.5 bg-[#D4AF37] hover:bg-[#c19d2f] text-black shrink-0" onClick={() => setAnimMakerOpen(true)}>
            <Wand2 className="w-3.5 h-3.5" /> Open
          </Button>
        </div>
      )}
      <TokenAnimationMakerModal open={animMakerOpen} onOpenChange={setAnimMakerOpen} gameId={game.id} applyToTokenId={null} />
      <div className="flex gap-1.5">
        {["token", "map"].map((k) => (
          <button
            key={k}
            data-testid={`assets-tab-filter-${k}`}
            onClick={() => setKindFilter(k)}
            className={`text-[10px] uppercase px-3 py-1 rounded-full border ${kindFilter === k ? "bg-[#D4AF37] border-[#D4AF37] text-black" : "border-white/10 text-[#f1e0c5]/50"}`}
          >
            {k === "token" ? "Token Art" : "Maps"}
          </button>
        ))}
      </div>
      <p className="text-[10px] text-[#f1e0c5]/30">Includes anything promoted to your Master Pool from the home-page Assets library.</p>
      <div className="grid grid-cols-3 gap-2">
        {assets.map((a) => (
          <div key={a.id} data-testid={`game-asset-${a.id}`} className="group relative rounded-lg overflow-hidden border border-white/10 bg-black/20 aspect-square">
            <img src={fileUrl(a.id)} alt={a.original_filename} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col items-center justify-center gap-1.5 p-1">
              <p className="text-[9px] text-center truncate w-full px-1">{a.original_filename}</p>
              {kindFilter === "token" && (
                <Button data-testid={`asset-spawn-npc-${a.id}`} size="sm" variant="secondary" className="rounded-full text-[10px] h-6 gap-1 px-2" onClick={() => spawnAsNpc(a)}>
                  <Sparkles className="w-3 h-3" /> Spawn NPC
                </Button>
              )}
              <button data-testid={`asset-delete-${a.id}`} onClick={() => deleteAsset(a.id)} className="text-[#f1e0c5]/60 hover:text-[#D90429]">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
      {assets.length === 0 && <p className="text-xs text-[#f1e0c5]/30 italic">No {kindFilter === "token" ? "token art" : "maps"} uploaded to this game yet.</p>}
    </div>
  );
}
